import { useEffect, useState } from 'react';
import { Alert, Box, Checkbox, FormControlLabel, Radio, Stack, TextField, Typography } from '@mui/material';
import { diApi } from '../../api';
import type { DataSourceSummary, RecordAction } from '../../types';
import { entityLabel, sourceTables } from '../../components/format';
import type { StepProps } from '../stepProps';
import { IssueList, entityNoun, issuesAt } from '../ConditionBuilder';

const ACTIONS: Array<{ value: RecordAction; label: string }> = [
  { value: 'create', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'delete', label: 'Deleted' }
];

export default function TriggerStep({ clientUid, pipeline, spec, onChange, catalogue }: StepProps) {
  const [source, setSource] = useState<DataSourceSummary | null>(null);

  useEffect(() => {
    let alive = true;
    diApi
      .getSources(clientUid)
      .then((r) => {
        if (alive) setSource((r.data || []).find((s) => s.uid === pipeline.sourceUid) ?? null);
      })
      .catch(() => undefined); // the source name is decorative here; the pipeline still edits without it
    return () => {
      alive = false;
    };
  }, [clientUid, pipeline.sourceUid]);

  const entity = spec.trigger.entity ?? pipeline.entity;
  const [one] = entityNoun(entity);
  // Every table the source has sent. This pipeline's own is always listed, even before its first record.
  const tableNames = sourceTables(source).map((t) => t.name);
  const entities = tableNames.includes(entity) ? tableNames : [entity, ...tableNames];
  const actions = spec.trigger.actions ?? [];
  const sourceName = source?.name ?? 'Aljex Live Sync';

  const toggle = (a: RecordAction, on: boolean) => {
    const set = new Set(actions);
    if (on) set.add(a);
    else set.delete(a);
    onChange({ ...spec, trigger: { ...spec.trigger, actions: ACTIONS.map((x) => x.value).filter((v) => set.has(v)) } });
  };

  return (
    <Stack spacing={2.25}>
      <Box>
        <Typography variant="h5">When should this pipeline run?</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Pick the kind of record it works on and which changes trigger it.
        </Typography>
      </Box>

      <IssueList issues={issuesAt(pipeline.draftValidation, ['trigger'])} />

      <Box sx={{ maxWidth: 420 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
          Source
        </Typography>
        <TextField fullWidth size="small" value={sourceName} disabled inputProps={{ 'aria-label': 'Source' }} />
        {source?.pipelinesStopped && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Pipelines are stopped for this source. Records are still stored, but no pipeline runs until it is resumed.
          </Alert>
        )}
      </Box>

      <Box component="fieldset" sx={{ border: 0, m: 0, p: 0 }}>
        <Typography component="legend" variant="subtitle2" sx={{ mb: 0.75 }}>
          Record type
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1.25 }}>
          {entities.map((e) => {
            const checked = e === entity;
            const count = source?.counts?.[e];
            return (
              <Box
                key={e}
                component="label"
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  border: '1px solid',
                  borderColor: checked ? 'primary.main' : 'divider',
                  bgcolor: checked ? 'primary.lighter' : 'background.paper',
                  borderRadius: '9px',
                  p: 1.25,
                  opacity: checked ? 1 : 0.6,
                  cursor: checked ? 'default' : 'not-allowed'
                }}
              >
                <Radio size="small" checked={checked} disabled={!checked} sx={{ p: 0.25 }} inputProps={{ 'aria-label': entityLabel(e) }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{entityLabel(e)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {count != null ? `${count.toLocaleString()} received` : '—'}
                    {checked && catalogue.length ? ` · ${catalogue.length.toLocaleString()} fields` : ''}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          The record type is fixed once a pipeline exists. Make a new pipeline for other records.
        </Typography>
      </Box>

      <Box component="fieldset" sx={{ border: 0, m: 0, p: 0 }}>
        <Typography component="legend" variant="subtitle2" sx={{ mb: 0.5 }}>
          Run when a {one} is
        </Typography>
        <Stack direction="row" spacing={2.25} flexWrap="wrap">
          {ACTIONS.map((a) => (
            <FormControlLabel
              key={a.value}
              control={<Checkbox size="small" checked={actions.includes(a.value)} onChange={(e) => toggle(a.value, e.target.checked)} />}
              label={<Typography variant="body2">{a.label} in Aljex</Typography>}
            />
          ))}
        </Stack>
        {!actions.length && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Pick at least one change, or this pipeline never runs.
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          fontSize: 13,
          color: 'text.secondary',
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          px: 1.5,
          py: 1.25
        }}
      >
        Almost everything Aljex sends is an update. An update that changes nothing this pipeline maps is skipped, so no transaction is
        rewritten and no rule fires twice.
      </Box>
    </Stack>
  );
}
